import {
  Heading,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  VStack,
  Center,
  Box,
  Image,
  useMediaQuery,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../components/firebaseConfig";
import { signOut } from "firebase/auth";
import Swal from "sweetalert2";
import { useAuthContext } from "./authContext";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { useLanguage } from "./languageContext";
import LanguageSwitch from "./LanguageSwitch";
import Navbar from "./NavBar";

interface UserForm {
  email: string;
  password: string;
  confirmPassword: string;
}

const handleSignOut = async () => {
  try {
    // Sign out from Firebase authentication
    await signOut(auth);
    // Navigate to the login page
    window.location.href = "/";
  } catch (error) {
    console.log("Error signing out:", error);
  }
};

const AddUser = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true); // Track whether passwords match
  const [isSmallScreen] = useMediaQuery("(max-width: 1100px)");
  const { language, toggleLanguage } = useLanguage();

  const navigate = useNavigate();

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    watch, // Access to the form fields' values
  } = useForm<UserForm>();

  const password = watch("password", "");
  const confirmPassword = watch("confirmPassword", "");

  useEffect(() => {
    setPasswordsMatch(password === confirmPassword);
  }, [password, confirmPassword]);

  const onSubmit: SubmitHandler<UserForm> = async (data) => {
    if (passwordsMatch) {
      try {
        const auth = getAuth();
        await createUserWithEmailAndPassword(auth, data.email, data.password);
        // User created successfully, you can navigate or show a success message here
        Swal.fire(
          language === "english"
            ? "User created successfully"
            : "Usuario creado exitosamente",
          "success"
        );
      } catch (error) {
        setError("email", {
          message:
            language === "english"
              ? "Failed to create user"
              : "Error al crear usuario",
        });
        Swal.fire(
          "Error",
          language === "english"
            ? "Failed to create user"
            : "Error al crear usuario",
          "error"
        );
      }
    } else {
      setError("confirmPassword", {
        message:
          language === "english"
            ? "Passwords do not match"
            : "Las contraseñas no coinciden",
      });
      Swal.fire(
        "Error",
        language === "english"
          ? "Passwords do not match"
          : "Las contraseñas no coinciden",
        "error"
      );
    }
  };

  return (
    <Box
    backgroundImage={"url(bird.jpeg)"}
    backgroundSize="cover"
    // backdropFilter="auto"
    // backdropBlur={"5px"}
  >
    <Navbar />
    <Center minH="100vh" >
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
          <Center >
            <Box
              p={4}
              pt={"10%"}
              width={{ base: "80%", md: "80%" }}
              maxW="1100px"
              minH="100vh"
              minW={{ md: "600px" }}
            >
              <Box p={8} bg="white" rounded="md" shadow="md" width="100%">
                <Stack
                  direction={isSmallScreen ? "column" : "row"}
                  spacing={{ base: 0, md: 0 }}
                  align={{ base: "center", md: "center" }} // Align logos vertically at the center
                  width={{ base: "100%", md: "100%", lg: "100%" }}
                  mx="auto"
                >
                  <Logo isSmallScreen={isSmallScreen} />
                </Stack>

                {/* EMAIL */}
                <FormControl
                  isInvalid={Boolean(errors.email)}
                  marginBottom="20px"
                >
                  <FormLabel
                    fontSize={{ base: "m", md: "l", lg: "l" }}
                    transition={"all 0.5s ease"}
                    fontWeight="700"
                  >
                    {language === "english" ? "EMAIL" : "CORREO ELECTRÓNICO"}
                  </FormLabel>
                  <Input
                    type="email"
                    {...register("email", {
                      required:
                        language === "english"
                          ? "This field is required"
                          : "Este campo es obligatorio",
                    })}
                  />
                  <FormErrorMessage fontWeight={700}>
                    {errors.email?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* PASSWORD */}
                <FormControl
                  isInvalid={Boolean(errors.password)}
                  marginBottom="20px"
                >
                  <FormLabel
                    fontWeight="700"
                    fontSize={{ base: "m", md: "l", lg: "l" }}
                    transition={"all 0.5s ease"}
                  >
                    {language === "english" ? "PASSWORD" : "CONTRASEÑA"}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      focusBorderColor="charcoal.500"
                      {...register("password", {
                        required:
                          language === "english"
                            ? "This field is required"
                            : "Este campo es obligatorio",
                      })}
                      type={showPassword ? "text" : "text"}
                    />
                  </InputGroup>
                  <FormErrorMessage fontWeight={700}>
                    {errors.password?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* CONFIRM PASSWORD */}
                <FormControl isInvalid={!passwordsMatch} marginBottom="20px">
                  <FormLabel
                    fontWeight="700"
                    fontSize={{ base: "m", md: "l", lg: "l" }}
                    transition={"all 0.5s ease"}
                  >
                    {language === "english"
                      ? "CONFIRM PASSWORD"
                      : "CONFIRMAR CONTRASEÑA"}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      focusBorderColor="charcoal.500"
                      {...register("confirmPassword", {
                        required:
                          language === "english"
                            ? "This field is required"
                            : "Este campo es obligatorio",
                      })}
                      type={showPassword ? "text" : "password"}
                    />
                    <InputRightElement>
                      <IconButton
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        variant="ghost"
                        aria-label={
                          showPassword
                            ? language === "english"
                              ? "Hide password"
                              : "Ocultar contraseña"
                            : language === "english"
                            ? "Show password"
                            : "Mostrar contraseña"
                        }
                        onClick={() =>
                          setShowPassword(
                            (prevShowPassword) => !prevShowPassword
                          )
                        }
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage fontWeight={700}>
                    {errors.confirmPassword?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* ADD USER BUTTON */}
                <Button
                  type="submit"
                  w="100%"
                  size={"lg"}
                  isDisabled={!passwordsMatch}
                >
                  {language === "english" ? "ADD USER" : "AGREGAR USUARIO"}
                </Button>
              </Box>
            </Box>
          </Center>
        </VStack>
      </form>
    </Center>
    </Box>
  );
};

export default AddUser;
